/**
 * Capability checks for the backoffice.
 *
 * The server is the source of truth: `/auth/me` returns the caller's permission
 * codenames and we store them verbatim. Nothing here branches on a role NAME, so
 * granting a role a new capability on the backend takes effect without a frontend
 * change.
 */

export const PERMISSIONS_KEY = 'permissions';
export const COUNTRIES_KEY = 'countries';
export const ROLE_KEY = 'userType';

// Capability codenames, mirroring portfolio/roles.py.
export const ACCESS_BACKOFFICE = 'portfolio.access_backoffice';
export const VIEW_UNPUBLISHED = 'portfolio.view_unpublished_proyecto';
export const PUBLISH_PROYECTO = 'portfolio.publish_proyecto';
export const ADD_PROYECTO = 'portfolio.add_proyecto';
export const CHANGE_PROYECTO = 'portfolio.change_proyecto';
export const DELETE_PROYECTO = 'portfolio.delete_proyecto';
export const VIEW_DRAFT = 'portfolio.view_proyectodraft';
export const DELETE_DRAFT = 'portfolio.delete_proyectodraft';
export const VIEW_USER = 'auth.view_user';
// Holding this lifts the country restriction; without it the user's own countries
// own the `iirsa_countries` field and they cannot change it.
export const CHANGE_ANY_COUNTRY = 'portfolio.change_any_country_proyecto';

export const ROLE_LABELS = {
  read_only: 'Solo lectura',
  country_manager: 'Gestor de país',
  administrator: 'Administrador',
  super_admin: 'Super administrador'
};

export const ROLE_CHOICES = Object.keys(ROLE_LABELS).map(id => ({ id, name: ROLE_LABELS[id] }));
export const ROLE_COUNTRY_MANAGER = 'country_manager';

export const storePermissions = identity => {
  localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(identity.permissions || []));
  localStorage.setItem(
    COUNTRIES_KEY,
    JSON.stringify((identity.countries || []).map(country => country.id))
  );
};

export const clearPermissions = () => {
  localStorage.removeItem(PERMISSIONS_KEY);
  localStorage.removeItem(COUNTRIES_KEY);
};

export const readPermissions = () => {
  try {
    return {
      role: localStorage.getItem(ROLE_KEY),
      codes: JSON.parse(localStorage.getItem(PERMISSIONS_KEY) || '[]'),
      countries: JSON.parse(localStorage.getItem(COUNTRIES_KEY) || '[]')
    };
  } catch (e) {
    return { role: null, codes: [], countries: [] };
  }
};

/**
 * Whether the current user holds a capability.
 *
 * Must tolerate an undefined `permissions`: react-admin v2 renders <Admin>'s
 * function child once before AUTH_GET_PERMISSIONS resolves.
 */
export const can = (permissions, code) =>
  !!(permissions && permissions.codes && permissions.codes.indexOf(code) !== -1);

/**
 * Country scope for the current user, read straight from localStorage.
 *
 * Forms are deep in the tree and react-admin v2 only injects `permissions` at the
 * route level, so reading it here avoids threading the prop through every form.
 * The server enforces the same rule regardless of what the form sends.
 */
export const countryScope = () => {
  const { codes, countries } = readPermissions();
  return {
    locked: codes.indexOf(CHANGE_ANY_COUNTRY) === -1,
    countries: countries || []
  };
};
